// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import _ from "lodash";
import React, { RefObject } from "react";
import SplitPane from "react-split-pane";
import {
    AssetState, AssetType, EditorMode, FieldType, IAsset, IAssetMetadata,
    ILabel, IRegion, ISize, FeatureCategory, TagInputMode, ITableTag,
    ITableRegion, AssetLabelingState, TableVisualizationHint, ITag,
    ILabelData, IFormRegion, ITableTagField, IValueObject, ITableTagFieldValue, IPredictionField, IPredictionResults, IAnalyzeResult,
} from "../../model/applicationState";
import { ILoadFileHelper, ILoadFileResult, LoadFileHelper } from "../prebuiltPredict/LoadFileHelper";
import Canvas from "../../Components/Canvas";
import CanvasHelpers from "../../Components/canvasHelpers";
import "./EditorPage.scss";
import Alert from "../../common/alert/alert";
import Confirm from "../../common/confirm/confirm";
import { constants } from "../../common/constants";
import { TagInput } from "../../common/tagInput/tagInput";
import { strings } from "../../common/strings";
import clone from "rfdc";
import { TableView } from "../TableView";
import { getPrimaryRedTheme } from "../../common/themes";
import styled from "styled-components";


const Wrapper = styled.div`
    .Resizer {
        -moz-box-sizing: border-box;
        -webkit-box-sizing: border-box;
        box-sizing: border-box;
        background: #000;
        opacity: 0.2;
        z-index: 1;
        -moz-background-clip: padding;
        -webkit-background-clip: padding;
        background-clip: padding-box;
    }
    .Resizer.horizontal {
        height: 11px;
        margin: -5px 0;
        border-top: 5px;
        border-bottom: 5px;
        cursor: row-resize;
        width: 100%;
    }
    .Resizer.vertical {
        width: 11px;
        margin: 0 -5px;
        border-left: 5px;
        border-right: 5px;
        cursor: col-resize;
    }
    .Pane.vertical.Pane1 {
        max-width: 35%;
        min-width: 20%;
    }
    .Pane.horizontal.Pane1 {
        overflow: auto;
    }
    .Pane.horizontal.Pane2 {
        height: 50%;
        max-height: 80%;
        min-height: 30%;
    }
`;

export interface IEditorPageProps {
    tags: ITag[],
    file: File,
    predictionResult: any,
    fileUrl: string,
    updatePredictionResult: (data: object, field: any, pageNumber: number) => void;
    onRegionChanged: () => void;
    submitValidation: (predictionResult: any, labelData: ILabelData) => void;
}


/**
 * State for Editor Page
 */
export interface IEditorPageState {
    /** Array of assets in project */
    assets: IAsset[];
    /** The editor mode to set for canvas tools */
    editorMode: EditorMode;
    /** The selected asset for the primary editing experience */
    selectedAsset?: IAssetMetadata;
    /** Currently selected region on current asset */
    selectedRegions?: IRegion[];
    /** Most recently selected tag */
    selectedTag: string;
    /** Tags locked for region labeling */
    lockedTags: string[];
    /** Size of the asset thumbnails to display in the side bar */
    thumbnailSize: ISize;
    /**
     * Whether or not the editor is in a valid state
     * State is invalid when a region has not been tagged
     */
    isValid: boolean;
    /** Whether the show invalid region warning alert should display */
    showInvalidRegionWarning: boolean;
    /** Show tags when loaded */
    tagsLoaded: boolean;
    /** The currently hovered TagInputItemLabel */
    hoveredLabel: ILabel;
    /** Whether the task for loading all OCRs is running */
    isRunningOCRs?: boolean;
    isRunningAutoLabelings?: boolean;
    /** Whether OCR is running in the main canvas */
    isCanvasRunningOCR?: boolean;
    isCanvasRunningAutoLabeling?: boolean;
    isError?: boolean;
    errorTitle?: string;
    errorMessage?: string;
    tableToView: object;
    tableToViewId: string;
    tagInputMode: TagInputMode;
    selectedTableTagToLabel: ITableTag;
    selectedTableTagBody: ITableRegion[][][];
    leftSplitPaneWidth?: number;
    reconfigureTableConfirm?: boolean;
    pageNumber: number;
    highlightedTableCellRegions: ITableRegion[];
    assetsLoaded: boolean;
    fileUploading: boolean;
    imageSize: {
        width: number,
        height: number
    }
    imageUri: string;
    labelData: ILabelData;
    selectedTableRow: number;
    selectedTableColumn: number;
    btmHeight: string;
}

/**
 * @name - Editor Page
 * @description - Page for adding/editing/removing tags to assets
 */
export default class EditorPage extends React.Component<IEditorPageProps, IEditorPageState> {
    public state: IEditorPageState = {
        selectedTag: null,
        lockedTags: [],
        assets: [],
        editorMode: EditorMode.Select,
        thumbnailSize: { width: 175, height: 155 },
        imageSize: {
            width: 0,
            height: 0,
        },
        imageUri: "",
        isValid: true,
        showInvalidRegionWarning: false,
        tagsLoaded: false,
        hoveredLabel: null,
        tableToView: null,
        tableToViewId: null,
        tagInputMode: TagInputMode.Basic,
        selectedTableTagToLabel: null,
        selectedTableTagBody: [[]],
        pageNumber: 1,
        highlightedTableCellRegions: null,
        assetsLoaded: false,
        fileUploading: false,
        labelData: null,
        selectedTableRow: null,
        selectedTableColumn: null,
        btmHeight: "",
    };

    private tagInputRef: RefObject<TagInput>;

    private canvas: RefObject<Canvas> = React.createRef();
    private replaceConfirmRef = React.createRef<Confirm>();
    private fileHelper: ILoadFileHelper = new LoadFileHelper();


    public initialLeftSplitPaneWidth: number;

    constructor(props) {
        super(props);
        this.tagInputRef = React.createRef();
    }

    public componentDidMount() {
        this.loadFile()
        this.setLabelData(this.props.predictionResult)
    }

    private setLabelData = (predictionResults: IPredictionResults) => {
        const getOcrFromAnalyzeResult = (analyzeResult: IAnalyzeResult) => {
            return _.get(analyzeResult, "readResults", []);
        }
        const getBoundingBox = (pageIndex, arr: number[]) => {
            const ocrForCurrentPage: any = getOcrFromAnalyzeResult(predictionResults.analyzeResult)[pageIndex - 1];
            const ocrExtent = [0, 0, ocrForCurrentPage.width, ocrForCurrentPage.height];
            const ocrWidth = ocrExtent[2] - ocrExtent[0];
            const ocrHeight = ocrExtent[3] - ocrExtent[1];
            const result = [];
            for (let i = 0; i < arr.length; i += 2) {
                result.push(...[
                    (arr[i] / ocrWidth),
                    (arr[i + 1] / ocrHeight),
                ]);
            }
            return result;
        };

        const getLabelValues = (field: IValueObject) => {
            return field.elements?.map((path: string): IFormRegion => {
                const pathArr = path.split('/').slice(1);
                const word = pathArr.reduce((obj: any, key: string) => obj[key], { ...predictionResults.analyzeResult });
                return {
                    page: field.page,
                    text: word.text || word.state,
                    boundingBoxes: [getBoundingBox(field.page, word.boundingBox)]
                };
            });
        };

        const getDynamicTableLabels = (fieldName: string, predictionField: IPredictionField) => {
            let tableLabels = [];
            if (!predictionField.valueArray) {
                return [];
            }
            for (const [rowIndex, row] of predictionField.valueArray.entries()) {
                for (const valueObject of Object.entries(row.valueObject)) {
                    const [key, props] = valueObject;
                    tableLabels.push({
                        label: `${fieldName}/${rowIndex}/${key}`,
                        key: null,
                        value: getLabelValues(props)
                    })
                }
            }
            return tableLabels;
        }

        const getFixedTableLabels = (fieldName: string, fieldProps: any) => {
            let tableLabels = [];

            for (const primaryKey of Object.keys(fieldProps.valueObject)) {
                const columnProps: ITableTagFieldValue = fieldProps.valueObject[primaryKey]
                for (const child of Object.entries(columnProps.valueObject)) {
                    const [childKey, childProps] = child;
                    tableLabels.push({
                        label: `${fieldName}/${primaryKey}/${childKey}`,
                        key: null,
                        value: getLabelValues(childProps)
                    })
                }
            }
            return tableLabels;
        }


        let labels = [];
        for (const documentResult of predictionResults.analyzeResult.documentResults) {
            const fields = documentResult.fields;
            for (const [fieldKey, predictionField] of Object.entries(fields)) {
                if (predictionField.type === FieldType.Array) {
                    const tableLabels = getDynamicTableLabels(fieldKey, predictionField);
                    labels = labels.concat(tableLabels);
                } else if (predictionField.type === FieldType.Object) {
                    const tableLabels = getFixedTableLabels(fieldKey, predictionField);
                    labels = labels.concat(tableLabels);
                } else {
                    labels.push({
                        label: fieldKey,
                        key: null,
                        confidence: fields[fieldKey].confidence,
                        value: getLabelValues(documentResult.fields[fieldKey])
                    })
                }
            }
        }

        this.setState({
            labelData: {
                "$schema": constants.labelsSchema,
                "document": this.props.file.name,
                labels
            }
        })
    }

    private getAssetType = () => {
        const { file } = this.props;
        if (file.type.includes("pdf")) return 5
        else if (file.type.includes("image") && !file.type.includes("tiff")) return 1
        else if (file.type.includes("tiff")) return 6
        else return 0
    }

    private updateAssetsState = () => {
        const asset: IAsset = {
            id: "",
            type: this.getAssetType(),
            state: 1,
            name: this.props.file.name,
            path: this.props.fileUrl,
            size: this.state.imageSize,
        }

        this.setState({
            selectedAsset: {
                asset,
                labelData: this.state.labelData,
                regions: [],
                version: "2.1.0"
            }
        })
    }

    private getPaneOrientation = (): "horizontal" | "vertical" => {
        if (this.state.selectedTableTagToLabel) {
            const paneOrientation = localStorage.getItem('paneOrientation')
            if (paneOrientation && paneOrientation == "horizontal") {
                return "horizontal";
            }
            return "vertical";
        }
        return "vertical";
    }

    private getPrimaryPane = (): "first" | "second" => {
        if (this.state.selectedTableTagToLabel) {
            const paneOrientation = localStorage.getItem('paneOrientation')
            if (paneOrientation && paneOrientation == "horizontal") {
                return "second";
            }
            return "first";
        }
        return "first";
    }

    private toggleBtmHeight = (topPaneHeight) => {
        this.setState({ btmHeight: topPaneHeight + "px" });
    }

    private changePaneOrientation = () => {
        this.forceUpdate()
    }

    public render() {
        const { selectedAsset } = this.state;

        const labels = (selectedAsset &&
            selectedAsset.labelData &&
            selectedAsset.labelData.labels) || [];
        const tableLabels = (selectedAsset &&
            selectedAsset.labelData &&
            selectedAsset.labelData.tableLabels) || [];

        const isBasicInputMode = this.state.tagInputMode === TagInputMode.Basic;
        this.initialLeftSplitPaneWidth = isBasicInputMode ? 290 : 520;

        return (
            <div className="editor-page skipToMainContent" id="pageEditor">
                {this.state.tableToView !== null &&
                    <TableView
                        handleTableViewClose={this.handleTableViewClose}
                        tableToView={this.state.tableToView}
                    />
                }
                <div className="editor-page-content" onClick={this.onPageClick}>
                    <Wrapper>
                        <SplitPane split={this.getPaneOrientation()}
                            primary={this.getPrimaryPane()}
                            defaultSize="70%"
                            pane1Style={{ height: `calc(100% - ${this.state.btmHeight})` }}
                            onChange={size => {
                                if (this.getPaneOrientation() === "horizontal") {
                                    this.toggleBtmHeight(size);
                                }
                                this.resizeCanvas();
                            }}
                        >
                            <div className="editor-page-left-sidebar">
                                <TagInput
                                    tagsLoaded={this.state.tagsLoaded}
                                    tags={this.props.tags}
                                    lockedTags={this.state.lockedTags}
                                    selectedRegions={this.state.selectedRegions}
                                    labels={labels}
                                    encoded={constants.supportedLabelsSchemas.has(selectedAsset?.labelData?.$schema)}
                                    tableLabels={tableLabels}
                                    pageNumber={this.state.pageNumber}
                                    onLockedTagsChange={this.onLockedTagsChanged}
                                    onTagClick={this.onTagClicked}
                                    onCtrlTagClick={this.onCtrlTagClicked}
                                    onLabelEnter={this.onLabelEnter}
                                    onLabelLeave={this.onLabelLeave}
                                    ref={this.tagInputRef}
                                    setTagInputMode={this.setTagInputMode}
                                    tagInputMode={this.state.tagInputMode}
                                    handleLabelTable={this.handleLabelTable}
                                    selectedTableTagToLabel={this.state.selectedTableTagToLabel}
                                    handleTableCellClick={this.handleTableCellClick}
                                    handleTableCellMouseEnter={this.handleTableCellMouseEnter}
                                    handleTableCellMouseLeave={this.handleTableCellMouseLeave}
                                    selectedTableTagBody={this.state.selectedTableTagBody}
                                    splitPaneWidth={this.state.leftSplitPaneWidth}
                                    addRowToDynamicTable={this.addRowToDynamicTable}
                                    deleteRowFromDynamicTable={this.deleteRowFromDynamicTable}
                                    onTagDoubleClick={this.onLabelDoubleClicked}
                                    onTagLabelEdit={this.updateTagLabel}
                                    onTableTagLabelEdit={this.onTableTagLabelEdited}
                                    predictionResult={this.props.predictionResult}
                                    updatePredictionResult={(result, field) => this.props.updatePredictionResult(result, field, this.state.pageNumber)}
                                    submitValidation={this.submitValidation}
                                    changePaneOrientation={this.changePaneOrientation}
                                />
                            </div>
                            <div className="editor-page-content-main" >
                                <div className="editor-page-content-main-body">
                                    {selectedAsset &&
                                        <Canvas
                                            ref={this.canvas}
                                            selectedAsset={this.state.selectedAsset}
                                            onAssetMetadataChanged={this.onAssetMetadataChanged}
                                            onSelectedRegionsChanged={this.onSelectedRegionsChanged}
                                            onRegionDoubleClick={this.onRegionDoubleClick}
                                            editorMode={this.state.editorMode}
                                            hoveredLabel={this.state.hoveredLabel}
                                            setTableToView={this.setTableToView}
                                            closeTableView={this.closeTableView}
                                            isRunningOCRs={this.state.isRunningOCRs}
                                            onPageLoaded={this.onPageLoaded}
                                            handleLabelTable={this.handleLabelTable}
                                            highlightedTableCell={this.state.highlightedTableCellRegions}
                                            ocr={this.props.predictionResult}
                                            tags={this.props.tags}
                                            onTagRegionsChanged={this.onTagRegionsChanged}
                                            handleRegionsDelete={this.handleRegionsDelete}>
                                        </Canvas>
                                    }
                                </div>
                            </div>
                        </SplitPane>
                    </Wrapper>
                </div>
                <Alert
                    show={this.state.showInvalidRegionWarning}
                    title={strings.editorPage.messages.enforceTaggedRegions.title}
                    // tslint:disable-next-line:max-line-length
                    message={strings.editorPage.messages.enforceTaggedRegions.description}
                    onClose={() => this.setState({ showInvalidRegionWarning: false })}
                />
                <Alert
                    show={this.state.isError}
                    title={this.state.errorTitle || "Error"}
                    message={this.state.errorMessage}
                    onClose={() => this.setState({
                        isError: false,
                        errorTitle: undefined,
                        errorMessage: undefined,
                    })}
                />
                <Confirm
                    title={strings.tags.warnings.replaceAllExitingLabelsTitle}
                    ref={this.replaceConfirmRef}
                    message={strings.tags.warnings.replaceAllExitingLabels}
                    confirmButtonTheme={getPrimaryRedTheme()}
                    onConfirm={this.onTableTagClicked}
                />
            </div>
        );
    }

    private deleteRowFromDynamicTable = async (rowIndex: number, tag: ITableTag) => {
        const selectedTableTagBody = clone()(this.state.selectedTableTagBody);

        this.deleteRowFromPredictionResults(rowIndex, tag);
        this.deleteRowFromAssetLabels(rowIndex, tag);
        selectedTableTagBody.splice(rowIndex, 1);
        this.setState({
            selectedTableTagBody,
        });
    }

    private deleteRowFromPredictionResults = (rowIndex: number, tag: ITableTag) => {
        let predictionResult = clone()(this.props.predictionResult);
        const { documentResults } = predictionResult.analyzeResult;
        let selectedTableTagField: ITableTagField = documentResults[0].fields[tag.name];
        selectedTableTagField.valueArray.splice(rowIndex, 1);
        predictionResult.analyzeResult.documentResults = documentResults;
        this.props.updatePredictionResult(predictionResult, selectedTableTagField, this.state.pageNumber);
    }

    private deleteRowFromAssetLabels = (rowIndex: number, tag: ITableTag) => {
        let selectedAsset = clone()(this.state.selectedAsset);
        const { labels } = selectedAsset.labelData;
        let newLabels = [];
        for (let label of labels) {
            let labelParts = label.label.split("/");
            const [name, row] = labelParts;
            if (name !== tag.name || parseInt(row) !== rowIndex) {
                if (parseInt(row) > rowIndex) {
                    labelParts[1] = (parseInt(row) - 1).toString();
                    label.label = labelParts.join("/");
                }
                newLabels.push(label)
            }
        }
        selectedAsset.labelData.labels = newLabels;
        this.setState({ selectedAsset });
        return selectedAsset;
    }

    private handleRegionsDelete = (regions: IRegion[]) => {
        const { documentResults } = this.props.predictionResult.analyzeResult;
        const { selectedAsset } = this.state;
        for (const region of regions) {
            if (region.isTableRegion) {
                const tagName = region.tags[0];
                const tag: ITableTag = this.props.tags.filter((t) => t.name === tagName)[0];
                if (tag.type === FieldType.Array) {
                    const rowIndex = region.rowKey.split("#")[1];
                    const labelName = `${tagName}/${rowIndex}/${region.columnKey}`;
                    const assetLabel = selectedAsset.labelData.labels.find(label => label.label === labelName);
                    if (assetLabel) {
                        const assetLabelText = assetLabel.value.map(value => value.text).join(" ");
                        this.updateTableTagLabel(tagName, rowIndex, region.columnKey, assetLabelText);
                    }
                } else {
                    let labelName, parentKey, childKey;
                    if (tag.visualizationHint === TableVisualizationHint.Horizontal) {
                        labelName = `${tagName}/${region.columnKey}/${region.rowKey}`;
                        parentKey = region.columnKey;
                        childKey = region.rowKey;
                    } else {
                        labelName = `${tagName}/${region.rowKey}/${region.columnKey}`;
                        parentKey = region.rowKey;
                        childKey = region.columnKey;
                    }
                    const assetLabel = selectedAsset.labelData.labels.find(label => label.label === labelName);
                    if (assetLabel) {
                        const assetLabelText = assetLabel.value.map(value => value.text).join(" ");
                        this.updateTableTagLabel(tagName, parentKey, childKey, assetLabelText)
                    }
                }
            } else {
                const tagName = region.tags[0];
                const assetLabel = selectedAsset.labelData.labels.find(label => label.label === tagName);
                if (assetLabel) {
                    const assetLabelText = assetLabel.value.map(value => value.text).join(" ");
                    this.updateTagLabel(tagName, assetLabelText)
                }
            }
        }
        const predictionResult = { ...this.props.predictionResult };
        predictionResult.analyzeResult.documentResults = documentResults;
        this.props.updatePredictionResult(predictionResult, null, this.state.pageNumber);
        this.props.onRegionChanged();
    }

    private onTableTagLabelEdited = (tagName: string, tableTagField: ITableTagField) => {
        if (tableTagField.type === FieldType.Array) {
            for (let rowIndex = 0; rowIndex < tableTagField.valueArray.length; rowIndex++) {
                const valueObject = tableTagField.valueArray[rowIndex].valueObject;
                for (const columnName of Object.keys(valueObject)) {
                    const { text } = valueObject[columnName];
                    this.updateTableTagLabel(tagName, rowIndex.toString(), columnName, text)
                }
            }
        } else if (tableTagField.type === FieldType.Object) {
            const tag: ITableTag = this.props.tags.filter((t) => t.name === tagName)[0];
            for (const tagFieldKey of this.getTagFieldKeys(tag)) {
                const parentValueObject = tableTagField.valueObject[tagFieldKey].valueObject;
                for (const tagDefinitionFieldKey of this.getTagDefinitionFieldKeys(tag)) {
                    const childValueObject = parentValueObject[tagDefinitionFieldKey];
                    this.updateTableTagLabel(tagName, tagFieldKey, tagDefinitionFieldKey, childValueObject.text)
                }
            }
        }
    }

    private getTagFieldKeys = (tag: ITableTag) => {
        return tag.fields.map(field => field.fieldKey);
    }

    private getTagDefinitionFieldKeys = (tag: ITableTag) => {
        return tag.definition.fields.map(field => field.fieldKey);
    }

    private updateTagLabel = (tagName: string, newText: string) => {
        const { documentResults } = this.props.predictionResult.analyzeResult;
        let tag = documentResults[0].fields[tagName];
        if (!tag) {
            tag = { type: "string", valueString: newText, text: newText, page: 1, boundingBox: [], elements: [] };
            documentResults[0].fields[tagName] = tag;
        }
        tag.text = newText;
        tag.fieldChanged = newText !== tag.valueString;
        this.props.updatePredictionResult(this.props.predictionResult, tag, this.state.pageNumber);
        return tag;
    }

    private updateTableTagLabel = (tagName: string, parentKey: string, childKey: string, newText: string) => {
        const { documentResults } = this.props.predictionResult.analyzeResult;

        let tagField: ITableTagField = documentResults[0].fields[tagName];
        let valueObject: IValueObject;
        if (tagField.type === FieldType.Object) {
            valueObject = tagField.valueObject[parentKey].valueObject;
        } else {
            valueObject = tagField.valueArray[parentKey].valueObject;
        }
        valueObject[childKey].fieldChanged = newText !== valueObject[childKey].valueString;
        valueObject[childKey].text = newText;
        this.props.updatePredictionResult(this.props.predictionResult, tagField, this.state.pageNumber);
        return tagField;
    }

    private onTagRegionsChanged = (tag: ITag | ITableTag, regions: IRegion[], label: ILabel) => {
        const tagRegions = regions.filter(region => region.tags[0] === tag.name);
        if (tag.type === FieldType.Array) {
            const regionsFromSelectedCell = tagRegions.filter(tagRegion => {
                const rowIndex = tagRegion.rowKey.split("#")[1];
                const labelName = `${tag.name}/${rowIndex}/${tagRegion.columnKey}`;
                return labelName === label.label
            });
            const cellText = regionsFromSelectedCell.map(tagRegion => tagRegion.value).join(" ");
            const [_, rowIndex, columnName] = label.label.split("/");
            this.updateTableTagLabel(tag.name, rowIndex, columnName, cellText)
        } else if (tag.type === FieldType.Object) {
            let tableTag = tag as ITableTag;
            const regionsFromSelectedCell = tagRegions.filter(tagRegion => {
                let labelName;
                if (tableTag.visualizationHint === TableVisualizationHint.Horizontal) {
                    labelName = `${tag.name}/${tagRegion.columnKey}/${tagRegion.rowKey}`;
                } else {
                    labelName = `${tag.name}/${tagRegion.rowKey}/${tagRegion.columnKey}`;
                }
                return labelName === label.label
            });
            const cellText = regionsFromSelectedCell.map(tagRegion => tagRegion.value).join(" ");
            const [_, parentKey, childKey] = label.label.split("/");
            this.updateTableTagLabel(tag.name, parentKey, childKey, cellText);
        } else {
            const tagText = tagRegions.map(tagRegion => tagRegion.value).join(" ");
            this.updateTagLabel(tag.name, tagText);
        }
        this.props.onRegionChanged();
    }

    private loadFile = () => {
        this.fileHelper.loadFile(this.props.file)
            .then((res: ILoadFileResult) => {
                if (res) {
                    this.setState({
                        imageSize: {
                            width: res.imageWidth,
                            height: res.imageHeight
                        },
                    }, () => {
                        this.updateAssetsState()
                    })
                }
            })
            .catch(err => {
                console.log(err)
            })
    }

    // tslint:disable-next-line:no-empty
    private onPageClick = () => {
    }

    private setTagInputMode = (tagInputMode: TagInputMode, selectedTableTagToLabel: ITableTag = this.state.selectedTableTagToLabel, selectedTableTagBody: ITableRegion[][][] = this.state.selectedTableTagBody) => {
        this.setState({
            selectedTableTagBody,
            selectedTableTagToLabel,
            tagInputMode,
        }, () => {
            this.resizeCanvas();
        });

    }

    private handleLabelTable = (tagInputMode: TagInputMode = this.state.tagInputMode, selectedTableTagToLabel: ITableTag = this.state.selectedTableTagToLabel) => {
        if (selectedTableTagToLabel == null || !this.state.selectedAsset) {
            return;
        }

        let rowKeys;
        let columnKeys;
        if (selectedTableTagToLabel.type === FieldType.Object) {
            if (selectedTableTagToLabel.visualizationHint === TableVisualizationHint.Vertical) {
                columnKeys = selectedTableTagToLabel.definition.fields;
                rowKeys = selectedTableTagToLabel.fields;
            } else {
                columnKeys = selectedTableTagToLabel.fields;
                rowKeys = selectedTableTagToLabel.definition.fields;

            }
        } else {
            rowKeys = null;
            columnKeys = selectedTableTagToLabel.definition.fields;
        }

        const selectedTableTagBody = new Array(rowKeys?.length || 0);
        if (this.state.selectedTableTagToLabel?.name === selectedTableTagToLabel?.name && selectedTableTagToLabel.type === FieldType.Array) {
            for (let i = 1; i < this.state.selectedTableTagBody.length; i++) {
                selectedTableTagBody.push(undefined)
            }
        }
        for (let i = 0; i < selectedTableTagBody.length; i++) {
            selectedTableTagBody[i] = new Array(columnKeys.length);
        }

        if (this.state.selectedAsset.regions.length === 0) {
            this.state.selectedAsset.regions = this.canvas.current?.convertLabelDataToRegions(this.state.labelData)
        }
        const tagAssets = clone()(this.state.selectedAsset.regions).filter((region) => region.tags[0] === selectedTableTagToLabel.name) as ITableRegion[];
        tagAssets.forEach((region => {
            let rowIndex: number;
            if (selectedTableTagToLabel.type === FieldType.Array) {
                rowIndex = Number(region.rowKey.slice(1));
            } else {
                rowIndex = rowKeys.findIndex(rowKey => rowKey.fieldKey === region.rowKey)
            }
            for (let i = selectedTableTagBody.length; i <= rowIndex; i++) {
                selectedTableTagBody.push(new Array(columnKeys.length));
            }
            const colIndex = columnKeys.findIndex(colKey => colKey.fieldKey === region.columnKey)
            if (selectedTableTagBody[rowIndex][colIndex] != null) {
                selectedTableTagBody[rowIndex][colIndex].push(region)
            } else {
                selectedTableTagBody[rowIndex][colIndex] = [region]
            }
        }));

        if (selectedTableTagToLabel.type === FieldType.Array) {
            const predictionField: ITableTagField = this.props.predictionResult.analyzeResult.documentResults[0].fields[selectedTableTagToLabel.name];
            predictionField.valueArray = predictionField.valueArray ? predictionField.valueArray : [];
            const rowsDifference = predictionField.valueArray.length - selectedTableTagBody.length;
            for (let i = 0; i < rowsDifference; i++) {
                selectedTableTagBody.push(new Array(columnKeys.length));
            }
        }

        this.setState({
            selectedTableTagToLabel,
            selectedTableTagBody,
        }, () => {
            this.setTagInputMode(tagInputMode);
        });
    }

    private addRowToDynamicTable = () => {
        this.addRowToLabelsData();
        this.addRowToPredictionResults();
        const { selectedTableTagBody } = clone()(this.state)
        selectedTableTagBody.push(Array(this.state.selectedTableTagToLabel.definition.fields.length));
        this.setState({ selectedTableTagBody });
    }

    private addRowToLabelsData = () => {
        let { selectedAsset, selectedTableTagToLabel } = clone()(this.state);
        const selectedTableTagField = this.props.predictionResult.analyzeResult.documentResults[0].fields[this.state.selectedTableTagToLabel.name];
        const columnNames = selectedTableTagToLabel.definition.fields.map(field => field.fieldKey);
        selectedTableTagField.valueArray = selectedTableTagField.valueArray ? selectedTableTagField.valueArray : [];
        columnNames.forEach(columnName => {
            const labelName = `${this.state.selectedTableTagToLabel.name}/${selectedTableTagField.valueArray.length}/${columnName}`;
            const label: ILabel = {
                label: labelName,
                key: null,
                value: []
            }
            selectedAsset.labelData.labels.push(label);
        })
        this.setState({ selectedAsset })
    }

    private addRowToPredictionResults = () => {
        const { name, definition: tableDefinition } = this.state.selectedTableTagToLabel;
        const { fields } = this.props.predictionResult.analyzeResult.documentResults[0];
        const tableTagField: ITableTagField = fields[name];
        const columnNames = tableDefinition.fields.map(field => field.fieldKey);
        let emptyRowValueObject = {};
        columnNames.forEach(columnName => {
            emptyRowValueObject[columnName] = {
                type: FieldType.String,
                valueString: "",
                fieldChanged: true,
                text: "",
                page: this.state.pageNumber,
                boundingBox: [],
                elements: [],
            }
        })
        let emptyRow: ITableTagFieldValue = {
            type: "object",
            valueObject: emptyRowValueObject
        }
        const predictionResult = { ...this.props.predictionResult };
        predictionResult.analyzeResult.documentResults[0].fields[name].valueArray.push(emptyRow);
        this.props.updatePredictionResult(predictionResult, tableTagField, this.state.pageNumber);
    }

    private handleTableCellClick = (rowIndex: number, columnIndex: number) => {
        if (!this.state.selectedRegions || this.state.selectedRegions.length === 0) {
            return;
        }

        if (this.state?.selectedTableTagBody?.[rowIndex]?.[columnIndex]?.length > 0) {
            const selectionRegionCatagory = this.state.selectedRegions[0].category;
            const cellCatagory = this.state.selectedTableTagBody[rowIndex][columnIndex][0].category;
            if (selectionRegionCatagory !== cellCatagory && (selectionRegionCatagory === FeatureCategory.DrawnRegion || cellCatagory === FeatureCategory.DrawnRegion)) {
                this.replaceConfirmRef.current.open(this.state.selectedTableTagToLabel, rowIndex, columnIndex);
                return;
            }
        }

        this.onTableTagClicked(this.state.selectedTableTagToLabel, rowIndex, columnIndex);
    }

    private handleTableCellMouseEnter = (regions) => {
        this.setState({ highlightedTableCellRegions: regions });
    }

    private handleTableCellMouseLeave = () => {
        this.setState({ highlightedTableCellRegions: null });
    }

    /**
     * Called when a tag from footer is clicked
     * @param tag Tag clicked
     */
    private onTagClicked = (tag: ITag): void => {
        this.setState({
            selectedTag: tag.name,
            lockedTags: [],
        }, () => {
            this.canvas.current.applyTag(tag.name)
        });
    }

    private onTableTagClicked = (tag: ITag, rowIndex: number, columnIndex: number): void => {
        this.setState({
            selectedTableRow: rowIndex,
            selectedTableColumn: columnIndex,
            selectedTag: tag.name,
            lockedTags: [],
        }, () => {
            this.canvas.current.applyTag(tag.name, rowIndex, columnIndex)
        });
    }

    private onCtrlTagClicked = (tag: ITag): void => {
        const locked = this.state.lockedTags;
        this.setState({
            selectedTag: tag.name,
            lockedTags: CanvasHelpers.toggleTag(locked, tag.name),
        }, () => this.canvas.current.applyTag(tag.name));
    }

    private submitValidation = () => {
        // console.log("Prediction results ", this.props.predictionResult)
        // console.log("LABEL DATA ", this.state.selectedAsset.labelData)
        this.props.submitValidation(this.props.predictionResult, this.state.selectedAsset.labelData);
    }

    /**
     * Returns a value indicating whether the current asset is taggable
     */
    private isTaggableAssetType = (asset: IAsset): boolean => {
        return asset.type !== AssetType.Unknown;
    }

    /**
     * Raised when the selected asset has been changed.
     * This can either be a parent or child asset
     */
    private onAssetMetadataChanged = async (assetMetadata: IAssetMetadata): Promise<void> => {
        // Comment out below code as we allow regions without tags, it would make labeler's work easier.
        assetMetadata = _.cloneDeep(assetMetadata);

        const asset = { ...assetMetadata.asset };

        if (this.isTaggableAssetType(asset)
            && asset.state !== AssetState.NotVisited
            && asset.labelingState !== AssetLabelingState.AutoLabeled
            && asset.labelingState !== AssetLabelingState.AutoLabeledAndAdjusted) {
            const hasLabels = _.get(assetMetadata, "labelData.labels.length", 0) > 0;
            const hasTableLabels = _.get(assetMetadata, "labelData.tableLabels.length", 0) > 0;

            if (hasLabels && assetMetadata.labelData.labels.findIndex(item => item?.value?.length > 0) >= 0) {
                asset.state = AssetState.Tagged
            } else if (hasTableLabels && assetMetadata.labelData.tableLabels.findIndex(item => item.labels?.length > 0) >= 0) {
                asset.state = AssetState.Tagged
            } else {
                asset.state = AssetState.Visited;
            }
        }

        // Only update asset metadata if state changes or is different
        if (this.state.selectedAsset !== assetMetadata) {
            if (JSON.stringify(assetMetadata.labelData) !== JSON.stringify(this.state.selectedAsset.labelData)) {
                this.setState({
                    selectedAsset: assetMetadata
                })
            }

            assetMetadata.asset = asset;

        }

        assetMetadata.regions = this.canvas.current?.convertLabelDataToRegions(assetMetadata.labelData) || [];

        // Find and update the root asset in the internal state
        // This forces the root assets that are displayed in the sidebar to
        // accurately show their correct state (not-visited, visited or tagged)
        this.setState((state) => {
            const assets = [...state.assets];
            const assetIndex = assets.findIndex((a) => a.id === asset.id);
            if (assetIndex > -1) {
                assets[assetIndex] = {
                    ...asset,
                };
            }
            return { assets, isValid: true };
        }, () => {
            this.handleLabelTable();
        });
    }

    private onSelectedRegionsChanged = (selectedRegions: IRegion[]) => {
        this.setState({ selectedRegions });
    }
    private onRegionDoubleClick = (region: IRegion) => {
        if (region.tags?.length > 0) {
            this.tagInputRef.current.focusTag(region.tags[0]);
        }
    }

    private onPageLoaded = async (pageNumber: number) => {
        this.setState({ pageNumber });
    }

    private onLockedTagsChanged = (lockedTags: string[]) => {
        this.setState({ lockedTags });
    }

    private onLabelEnter = (label: ILabel) => {
        this.setState({ hoveredLabel: label });
    }

    private onLabelDoubleClicked = (label: ILabel) => {
        this.canvas.current.focusOnLabel(label);
    }

    private onLabelLeave = (label: ILabel) => {
        this.setState({ hoveredLabel: null });
    }

    private setTableToView = async (tableToView, tableToViewId) => {
        if (this.state.tableToViewId) {
            this.canvas.current.setTableState(this.state.tableToViewId, "rest");
        }
        this.canvas.current.setTableState(tableToViewId, "selected");
        this.setState({
            tableToView,
            tableToViewId,
        });
    }

    private handleTableViewClose = () => {
        this.closeTableView("rest");
    }

    private closeTableView = (state: string) => {
        if (this.state.tableToView) {
            this.canvas.current.setTableState(this.state.tableToViewId, state);
            this.setState({
                tableToView: null,
                tableToViewId: null,
            });
        }
    }

    private resizeCanvas = () => {
        if (this.canvas.current) {
            this.canvas.current.updateSize();
        }
    }
}
